import { t } from "@travily/i18n";
import { AddIcon, ExitIcon, MaximizeIcon, MinusIcon } from "@ui/components/Icon";
import type { MouseEvent } from "react";
import { MAP_CHROME_BUTTON, MAP_CHROME_ROUND_BUTTON_LG, MAP_CHROME_SURFACE } from "./chrome";

export interface MapControlsProps {
  onZoomIn: (event: MouseEvent) => void;
  onZoomOut: (event: MouseEvent) => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  /** Show the expand/close toggle (omit on maps that can't expand, e.g. admin pickers). */
  showFullscreen: boolean;
  /** Corner to anchor the controls. Use `bottom-right` on maps with content overlaying
   *  the top edge. Defaults to `top-right`. */
  position?: "top-right" | "bottom-right";
}

/**
 * Custom map controls replacing Mapbox's stock NavigationControl: a zoom +/- pill,
 * optionally topped by an expand/close (X) toggle, anchored top-right (or bottom-right
 * via `position`). The toggle shows an expand icon collapsed and a close (X) when
 * expanded. 24px inset, 16px gap.
 */
export function MapControls({
  onZoomIn,
  onZoomOut,
  isFullscreen,
  onToggleFullscreen,
  showFullscreen,
  position = "top-right",
}: MapControlsProps) {
  return (
    <div
      className={
        position === "bottom-right"
          ? "absolute right-6 bottom-6 z-10 flex flex-col items-end gap-4"
          : "absolute top-6 right-6 z-10 flex flex-col items-end gap-4"
      }
    >
      {showFullscreen ? (
        <button
          aria-label={isFullscreen ? t("components.map.fullscreen_exit") : t("components.map.fullscreen_enter")}
          className={MAP_CHROME_ROUND_BUTTON_LG}
          onClick={onToggleFullscreen}
          type="button"
        >
          {isFullscreen ? <ExitIcon aria-hidden className="size-5" /> : <MaximizeIcon aria-hidden className="size-5" />}
        </button>
      ) : null}
      <div className={`flex w-10 flex-col overflow-hidden rounded-full ${MAP_CHROME_SURFACE}`}>
        <button
          aria-label={t("components.map.zoom_in")}
          className={`h-10 ${MAP_CHROME_BUTTON}`}
          onClick={onZoomIn}
          type="button"
        >
          <AddIcon aria-hidden className="size-5" />
        </button>
        <div className="h-px w-full shrink-0 bg-neutral-100" />
        <button
          aria-label={t("components.map.zoom_out")}
          className={`h-10 ${MAP_CHROME_BUTTON}`}
          onClick={onZoomOut}
          type="button"
        >
          <MinusIcon aria-hidden className="size-5" />
        </button>
      </div>
    </div>
  );
}
